import { useQuery } from "@tanstack/react-query";
import {
  Loader2, TrendingUp, AlertTriangle, Clock, Activity, DollarSign, CircleDot, Wifi,
} from "lucide-react";
import api from "@/lib/api";
import { STATUS_COLOR, PRIORITY_COLOR, moneyExact, timeAgo, initials } from "@/lib/constants";

function Widget({ title, icon: Icon, children, testId }) {
  return (
    <div className="bg-card border border-border rounded-md" data-testid={testId}>
      <div className="flex items-center gap-2 px-4 h-11 border-b border-border">
        <Icon className="h-4 w-4 text-primary" />
        <span className="label-caps !text-foreground">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function DashboardTab({ projectId, currency }) {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/dashboard`)).data,
    refetchInterval: 8000,
  });
  const { data: activity } = useQuery({
    queryKey: ["activity", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/activity`)).data.activities,
    refetchInterval: 8000,
  });

  if (isLoading || !data) {
    return <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const f = data.financials;
  const t = data.task_stats;
  const budgetPct = Math.min(f.budget_used_pct, 100);
  const overBudget = f.budget_remaining < 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Wifi className="h-3.5 w-3.5 text-[#10B981]" />
        <span>Live · auto-refreshing every 8s</span>
      </div>

      {/* KPI strip */}
      <div className="grid gap-px bg-border border border-border rounded-md overflow-hidden sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Total Budget", value: moneyExact(f.budget, currency), sub: "Approved" },
          { label: "Actual Cost", value: moneyExact(f.actual_cost_total, currency), sub: `${f.budget_used_pct}% used`, accent: true },
          { label: "Est. Cost (Tasks)", value: moneyExact(f.estimated_cost_total, currency), sub: "Planned" },
          { label: "Remaining", value: moneyExact(f.budget_remaining, currency), sub: overBudget ? "Over budget" : "Available", danger: overBudget },
        ].map((k) => (
          <div key={k.label} className="bg-card p-4">
            <div className="label-caps flex items-center gap-1"><DollarSign className="h-3 w-3" />{k.label}</div>
            <div className={`font-data font-bold text-xl mt-2 ${k.accent ? "text-primary" : k.danger ? "text-destructive" : ""}`}>{k.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Financial tracking */}
        <Widget title="Financial Tracking" icon={TrendingUp} testId="widget-financial">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-muted-foreground">Budget Consumption</span>
                <span className="font-data font-bold">{f.budget_used_pct}%</span>
              </div>
              <div className="h-3 bg-background rounded overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${budgetPct}%`, backgroundColor: overBudget ? "#EF4444" : "#F59E0B" }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div>
                <div className="label-caps">Est vs Actual</div>
                <div className="font-data font-bold text-lg mt-1">{moneyExact(f.actual_cost_total, currency)}</div>
                <div className="text-xs text-muted-foreground">of {moneyExact(f.estimated_cost_total, currency)} est.</div>
              </div>
              <div>
                <div className="label-caps">Variance</div>
                <div className={`font-data font-bold text-lg mt-1 ${f.variance < 0 ? "text-destructive" : "text-[#10B981]"}`}>
                  {moneyExact(f.variance, currency)}
                </div>
                <div className="text-xs text-muted-foreground">est − actual</div>
              </div>
            </div>
          </div>
        </Widget>

        {/* Task distribution */}
        <Widget title="Task Distribution" icon={CircleDot} testId="widget-tasks">
          <div className="mb-3 flex items-baseline gap-2">
            <span className="font-data font-black text-3xl">{t.completion_pct}%</span>
            <span className="text-sm text-muted-foreground">complete ({t.done}/{t.total})</span>
          </div>
          <div className="space-y-2">
            {Object.entries(t.status_counts).map(([s, count]) => {
              const pct = t.total ? (count / t.total) * 100 : 0;
              return (
                <div key={s}>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: STATUS_COLOR[s] }} className="font-semibold">{s}</span>
                    <span className="font-data">{count}</span>
                  </div>
                  <div className="h-1.5 bg-background rounded overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, backgroundColor: STATUS_COLOR[s] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Widget>

        {/* Priority mix */}
        <Widget title="Priority Mix" icon={AlertTriangle} testId="widget-priority">
          <div className="space-y-3">
            {Object.entries(t.priority_counts).map(([p, count]) => (
              <div key={p} className="flex items-center justify-between border border-border rounded px-3 h-12">
                <span className="flex items-center gap-2 font-semibold text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PRIORITY_COLOR[p] }} />{p}
                </span>
                <span className="font-data font-bold text-lg">{count}</span>
              </div>
            ))}
          </div>
        </Widget>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Milestones */}
        <Widget title="Schedule Milestones" icon={Clock} testId="widget-milestones">
          {data.milestones.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No upcoming deadlines.</p>
          ) : (
            <div className="space-y-2">
              {data.milestones.map((m) => (
                <div key={m.id} className="flex items-center gap-3 border border-border rounded px-3 py-2.5 min-h-12">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLOR[m.status] }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{m.title}</div>
                    <div className="text-xs text-muted-foreground font-data">{m.due_date}</div>
                  </div>
                  {m.overdue && <span className="text-xs font-bold text-destructive uppercase">Overdue</span>}
                  {!m.overdue && m.due_soon && <span className="text-xs font-bold text-primary uppercase">Due Soon</span>}
                </div>
              ))}
            </div>
          )}
        </Widget>

        {/* Critical / blocked */}
        <Widget title="Critical & Blocked" icon={AlertTriangle} testId="widget-critical">
          {data.critical_blocked.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Nothing critical or blocked. Good.</p>
          ) : (
            <div className="space-y-2">
              {data.critical_blocked.map((m) => (
                <div key={m.id} className="flex items-center gap-3 border border-destructive/40 bg-destructive/5 rounded px-3 py-2.5 min-h-12">
                  <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{m.title}</div>
                    <div className="text-xs text-muted-foreground">{m.status} · {m.priority}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Widget>
      </div>

      {/* Activity stream */}
      <Widget title="Global Activity Stream" icon={Activity} testId="widget-activity">
        {!activity || activity.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No recent activity.</p>
        ) : (
          <div className="space-y-1">
            {activity.map((a) => (
              <div key={a.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <div className="h-8 w-8 rounded bg-secondary grid place-items-center text-xs font-data font-bold shrink-0">
                  {initials(a.actor?.first_name, a.actor?.last_name)}
                </div>
                <div className="flex-1 min-w-0 text-sm">
                  <span className="font-semibold">{a.actor?.first_name || "Someone"}</span>{" "}
                  <span className="text-muted-foreground">{a.description}</span>
                </div>
                <span className="text-xs text-muted-foreground font-data shrink-0">{timeAgo(a.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Widget>
    </div>
  );
}
